import type { TranslationKey } from '~/shared/internationalization/translation';
import { IconAlignment, IconArrowRightUpCircle, IconLevel, IconListSparkle, IconStop, IconTag } from '~/ui/core/icons';

import type {
    AiTransformationStatus,
    AiTransformationSubject,
} from '../../../data-contract/ai-transformation-strategy';

type StatusPresentation = {
    /** The status's dot and bar colour. */
    readonly dotClassName: string;
    readonly Icon: typeof IconStop;
    readonly shortLabelKey: TranslationKey;
    /** The same colour for a symbol or text. */
    readonly textClassName: string;
};

/**
 * How each status looks. The order of the statuses comes from the backend's filters response and where an item may
 * move from the item's own `movableTo`, never from here.
 */
const STATUS_PRESENTATION: Readonly<Record<AiTransformationStatus, StatusPresentation>> = {
    DEFINED: {
        dotClassName: 'bg-status-info-border',
        Icon: IconAlignment,
        shortLabelKey: 'pages.ai-transformation.strategy.status-short.defined',
        textClassName: 'text-status-info-border',
    },
    FULL: {
        dotClassName: 'bg-status-success-emphasis',
        Icon: IconListSparkle,
        shortLabelKey: 'pages.ai-transformation.strategy.status-short.full',
        textClassName: 'text-status-success-emphasis',
    },
    IDLE: {
        dotClassName: 'bg-text-muted',
        Icon: IconStop,
        shortLabelKey: 'pages.ai-transformation.strategy.status-short.idle',
        textClassName: 'text-text-muted',
    },
    PRIORITIZED: {
        dotClassName: 'bg-status-warning-text',
        Icon: IconTag,
        shortLabelKey: 'pages.ai-transformation.strategy.status-short.prioritized',
        textClassName: 'text-status-warning-text',
    },
    SCALING: {
        dotClassName: 'bg-status-info-text',
        Icon: IconLevel,
        shortLabelKey: 'pages.ai-transformation.strategy.status-short.scaling',
        textClassName: 'text-status-info-text',
    },
    STARTING: {
        dotClassName: 'bg-status-info-text',
        Icon: IconArrowRightUpCircle,
        shortLabelKey: 'pages.ai-transformation.strategy.status-short.starting',
        textClassName: 'text-status-info-text',
    },
};

const LABEL_KEYS: Readonly<Record<Exclude<AiTransformationStatus, 'FULL'>, TranslationKey>> = {
    DEFINED: 'pages.ai-transformation.strategy.status.defined',
    IDLE: 'pages.ai-transformation.strategy.status.idle',
    PRIORITIZED: 'pages.ai-transformation.strategy.status.prioritized',
    SCALING: 'pages.ai-transformation.strategy.status.scaling',
    STARTING: 'pages.ai-transformation.strategy.status.starting',
};

export const statusPresentation = (status: AiTransformationStatus): StatusPresentation => STATUS_PRESENTATION[status];

/** The full name; the last status names what is automated, a position or a task. */
export const statusLabelKey = (status: AiTransformationStatus, subject: AiTransformationSubject): TranslationKey => {
    if (status !== 'FULL') return LABEL_KEYS[status];

    return subject === 'TASK'
        ? 'pages.ai-transformation.strategy.status.full-task'
        : 'pages.ai-transformation.strategy.status.full-position';
};
